/**
 * The two provider choices, the credential each one needs, and the models and
 * voice they are asked for.
 *
 * Split out of `TranscriptionCard` rather than grown inside it: the card owns
 * loading, saving and testing, and this owns what the operator is choosing
 * between. Two responsibilities, and the card was already at the size where the
 * form starts hiding the logic.
 *
 * ⚠ THE TWO SWITCHES ARE INDEPENDENT AND THE PAGE HAS TO MAKE THAT OBVIOUS.
 * "Read the calls" and "speak the announcements" are separate purchases: Scribe
 * is worth paying for because it reads Estonian properly, while TartuNLP already
 * speaks every announcement this installation has and costs nothing. Crossing
 * them is a normal answer, not a misconfiguration, so they are two rows rather
 * than one "use ElevenLabs" tick.
 *
 * NOTHING ON THIS TAB IS A NATIVE `<select>` OR A BARE TEXT BOX ANY MORE, and
 * both halves of that are the same complaint. The operating system's drop-down
 * drapes a grey list over a form of pale pills - the reason `MenuField` exists
 * at all - and a text box for a model id says nothing about which ids exist,
 * which mattered most for exactly the field where a typo is invisible:
 * `eleven_v3` is the only ElevenLabs model that speaks Estonian, and any of the
 * other four returns fluent audio in the wrong mouth. So the providers are the
 * app's own menu, and the three model fields plus the voice are searchable
 * pickers that still accept anything typed - see `PickerField` and
 * `speechModels.ts`.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type {
  TelephonyStatus,
  TranscribeProvider,
  TtsProvider,
  TtsVoiceListing,
} from "../../../../lib/calls";
import { MenuField, PickerField, type PickerOption } from "../../../ui";
import { Field, FieldBox, FieldGroup, Note, Pill, SecretPill } from "../panel/fields";
import { bareVoiceId, speaksEstonian } from "./noticeClip";
import { ELEVEN_TTS_MODELS, GEMINI_MODELS, SCRIBE_MODELS, type SpeechModelChoice } from "./speechModels";

export interface SpeechProviderDraft {
  transcribe: TranscribeProvider;
  tts: TtsProvider;
  geminiKey: string;
  geminiModel: string;
  /** Requests per Pacific day, both consumers together. "0" or "" means no cap. */
  geminiDailyCap: string;
  /** How many of those are held back for transcripts. */
  geminiReserve: string;
  elevenKey: string;
  elevenSttModel: string;
  elevenTtsModel: string;
  /** Bare ElevenLabs voice id - the group already names the service. */
  elevenVoiceId: string;
}

export function SpeechProviderFields({
  status,
  draft,
  voices,
  onChange,
}: {
  status: TelephonyStatus | null;
  draft: SpeechProviderDraft;
  /** The live voice list, or null while it is in flight. */
  voices: TtsVoiceListing | null;
  onChange: (patch: Partial<SpeechProviderDraft>) => void;
}) {
  const t = useT();
  // The fields for a provider that is not chosen are still editable, and that is
  // the order somebody sets this up in: paste the key, press test, and only then
  // move the switch. Hiding them until the switch moves would mean flipping a
  // live setting to find out whether the key works.
  const usesEleven = draft.transcribe === "elevenlabs" || draft.tts === "elevenlabs";

  /** One model list as picker rows: the id is the value, the prose is the hint. */
  function modelOptions(models: SpeechModelChoice[]): PickerOption[] {
    return models.map((model) => ({
      value: model.id,
      label: model.id,
      hint: t(`settings.transcription.modelNotes.${model.note}`),
    }));
  }

  /**
   * The ElevenLabs workspace as picker rows, plus the "let it choose" row.
   *
   * Only that provider's half of the list: this field IS the ElevenLabs default,
   * and offering a Tartu speaker in it would be storing a voice under a key the
   * synthesis endpoint will never be asked with. The announcement pickers are
   * the ones that offer both - see `NoticeClipRow`.
   *
   * The empty row is first and is a real answer, not a placeholder: "whatever
   * the workspace lists first" is what an installation that has never opened
   * this field is on, and it has to be possible to go back to it.
   */
  function voiceOptionsForDefault(): PickerOption[] {
    const rows = (voices?.voices ?? [])
      .filter((voice) => voice.provider === "elevenlabs")
      .map((voice) => ({
        value: bareVoiceId(voice),
        label: voice.label,
        hint: speaksEstonian(voice)
          ? t("settings.transcription.voiceEstonian")
          : voice.languages.join(", ") || bareVoiceId(voice),
      }));
    return [{ value: "", label: t("settings.transcription.voiceAuto") }, ...rows];
  }

  const elevenVoices = (voices?.voices ?? []).filter((voice) => voice.provider === "elevenlabs");
  // The list is fetched for the SAVED key, so a key pasted in the field above and
  // not saved yet cannot produce voices. Said out loud below rather than left as
  // an empty menu somebody reads as a broken picker.
  const voicesPending = !!draft.elevenKey && elevenVoices.length === 0;

  return (
    <div className="space-y-6">
      <FieldGroup title={t("settings.transcription.providerGroup")}>
        <div className="grid gap-4 sm:grid-cols-2">
          <FieldBox
            label={t("settings.transcription.transcribeProvider")}
            hint={t("settings.transcription.transcribeProviderHint")}
          >
            <MenuField
              variant="form"
              value={draft.transcribe}
              onChange={(value) => onChange({ transcribe: value })}
              label={t("settings.transcription.transcribeProvider")}
              options={[
                { value: "gemini", label: t("settings.transcription.providerGemini") },
                { value: "elevenlabs", label: t("settings.transcription.providerScribe") },
              ]}
            />
          </FieldBox>

          <FieldBox
            label={t("settings.transcription.ttsProvider")}
            hint={t("settings.transcription.ttsProviderHint")}
          >
            <MenuField
              variant="form"
              value={draft.tts}
              onChange={(value) => onChange({ tts: value })}
              label={t("settings.transcription.ttsProvider")}
              options={[
                { value: "tartunlp", label: t("settings.transcription.providerTartu") },
                { value: "elevenlabs", label: t("settings.transcription.providerEleven") },
              ]}
            />
          </FieldBox>
        </div>

        {/* The one consequence of the transcription switch that is not obvious
            from the switch itself, and the reason the Gemini fields stay below
            it rather than disappearing when ElevenLabs is chosen. */}
        {draft.transcribe === "elevenlabs" ? (
          <Note tone="info">{t("settings.transcription.scribeSplitNote")}</Note>
        ) : null}
        {draft.transcribe === "elevenlabs" && status && !status.providers.summaryReady ? (
          <Note tone="warn">{t("settings.transcription.noSummaryWarn")}</Note>
        ) : null}
      </FieldGroup>

      <FieldGroup title={t("settings.transcription.geminiGroup")}>
        <Note tone="info">{t("settings.transcription.independentNote")}</Note>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("settings.transcription.apiKey")}
            hint={
              status?.gemini.apiKeySet && !draft.geminiKey
                ? t("settings.transcription.secretKept")
                : undefined
            }
          >
            <SecretPill
              value={draft.geminiKey}
              onChange={(value) => onChange({ geminiKey: value })}
              placeholder={status?.gemini.apiKeySet ? "••••••••" : "AIza..."}
            />
          </Field>

          <FieldBox
            label={t("settings.transcription.model")}
            hint={t("settings.transcription.modelHint")}
          >
            <PickerField
              mono
              allowCustom
              value={draft.geminiModel}
              onChange={(value) => onChange({ geminiModel: value })}
              options={modelOptions(GEMINI_MODELS)}
              label={t("settings.transcription.model")}
              searchPlaceholder={t("settings.transcription.modelSearch")}
              customLabel={t("settings.transcription.modelCustom")}
              emptyLabel={t("settings.transcription.modelNone")}
            />
          </FieldBox>

          {/* The ceiling and the share of it that transcripts keep.
              ZERO IS OFF, and the hint says so rather than leaving somebody to
              discover it: this pair arrives on installations that have been
              transcribing for months, and a blank that meant "send nothing"
              would stop every transcript on the day it shipped. */}
          <Field
            label={t("settings.transcription.dailyCap")}
            hint={t("settings.transcription.dailyCapHint")}
          >
            <Pill
              className="h-10 max-w-[120px]"
              value={draft.geminiDailyCap}
              onChange={(value) => onChange({ geminiDailyCap: value })}
              inputMode="numeric"
              placeholder="0"
            />
          </Field>
          <Field
            label={t("settings.transcription.reserve")}
            hint={t("settings.transcription.reserveHint")}
          >
            <Pill
              className="h-10 max-w-[120px]"
              value={draft.geminiReserve}
              onChange={(value) => onChange({ geminiReserve: value })}
              inputMode="numeric"
              placeholder="0"
            />
          </Field>
        </div>
      </FieldGroup>

      <FieldGroup title={t("settings.transcription.elevenGroup")}>
        <Note tone="info">{t("settings.transcription.elevenAbout")}</Note>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t("settings.transcription.elevenKey")}
            hint={
              status?.elevenlabs.apiKeySet && !draft.elevenKey
                ? t("settings.transcription.secretKept")
                : undefined
            }
          >
            <SecretPill
              value={draft.elevenKey}
              onChange={(value) => onChange({ elevenKey: value })}
              placeholder={status?.elevenlabs.apiKeySet ? "••••••••" : "sk_..."}
            />
          </Field>

          <FieldBox
            label={t("settings.transcription.elevenSttModel")}
            hint={t("settings.transcription.elevenSttModelHint")}
          >
            <PickerField
              mono
              allowCustom
              value={draft.elevenSttModel}
              onChange={(value) => onChange({ elevenSttModel: value })}
              options={modelOptions(SCRIBE_MODELS)}
              label={t("settings.transcription.elevenSttModel")}
              searchPlaceholder={t("settings.transcription.modelSearch")}
              customLabel={t("settings.transcription.modelCustom")}
              emptyLabel={t("settings.transcription.modelNone")}
            />
          </FieldBox>

          <FieldBox
            label={t("settings.transcription.elevenTtsModel")}
            hint={t("settings.transcription.elevenTtsModelHint")}
          >
            <PickerField
              mono
              allowCustom
              value={draft.elevenTtsModel}
              onChange={(value) => onChange({ elevenTtsModel: value })}
              options={modelOptions(ELEVEN_TTS_MODELS)}
              label={t("settings.transcription.elevenTtsModel")}
              searchPlaceholder={t("settings.transcription.modelSearch")}
              customLabel={t("settings.transcription.modelCustom")}
              emptyLabel={t("settings.transcription.modelNone")}
            />
          </FieldBox>

          {/* The workspace, not a list this build ships. A voice cloned an hour
              ago has to be offered without a release, and an id typed by hand is
              still accepted - which is the only thing that works while the key
              is saved but the list has not come back. */}
          <FieldBox
            label={t("settings.transcription.elevenVoice")}
            hint={t("settings.transcription.elevenVoiceHint")}
          >
            <PickerField
              allowCustom
              value={draft.elevenVoiceId}
              onChange={(value) => onChange({ elevenVoiceId: value })}
              options={voiceOptionsForDefault()}
              label={t("settings.transcription.elevenVoice")}
              placeholder={t("settings.transcription.voiceAuto")}
              searchPlaceholder={t("settings.transcription.voiceSearch")}
              customLabel={t("settings.transcription.voiceCustom")}
              emptyLabel={t("settings.transcription.voiceNone")}
            />
          </FieldBox>
        </div>

        {/* The silent failure worth a loud warning: the wrong synthesis model
            returns fluent audio of Estonian words in another language's mouth,
            which previews perfectly and sounds wrong only on the phone. */}
        {status?.providers.ttsModelLacksEstonian ? (
          <Note tone="warn">{t("settings.transcription.ttsModelWarn")}</Note>
        ) : null}
        {usesEleven && status && !status.elevenlabs.apiKeySet ? (
          <Note tone="warn">{t("settings.transcription.elevenNeedsKey")}</Note>
        ) : null}
        {/* A key in the box and nothing in the voice menu is not a failure, it
            is an order of operations - the list is fetched for the SAVED key. */}
        {voicesPending ? <Note tone="mut">{t("settings.transcription.voicesAfterSave")}</Note> : null}
        {voices?.truncated ? <Note tone="mut">{t("settings.transcription.voicesTruncated")}</Note> : null}
      </FieldGroup>
    </div>
  );
}
